// src/lib/purchaseOrder.js
// Session 78n8.2, Issue #395 (+ #257). Gate decisions D3 and D4 in
// docs/gates/Stede_DocumentOutput_Gate_395.html, approved by Colby at 78n5.
//
// WHAT THIS IS, in plain words: the purchase order Jennifer sends to a vendor.
// She finishes the Mark as ordered sheet she already fills out, and this file
// turns what she typed into an actual document -- a letterhead, who it is from,
// where it ships to, the lines with their prices, and a total.
//
// WHY IT IS A PURE MODULE AND NOT JSX: the same document has to come out in
// THREE places and they must not disagree.
//   1. the HTML email body the vendor reads (D3)
//   2. the PDF attached to that same email for the vendor's filing (D3)
//   3. the on-screen preview Jennifer checks before she sends
// Three renderers deriving "what does the order say" independently is how two of
// them end up wrong. So the MODEL is built once, here, and the renderers only
// draw it. src/lib/purchaseOrderPdf.js draws the PDF from this same model.
//
// It is also the only way this code can be tested at all before it reaches a
// supplier: `sendEmail` suppresses every send outside production, so the email
// path cannot be exercised on localhost. A pure model with unit tests is the
// substitute for that, and it is the same reasoning that put #405's validation
// in functions/_shared/mailPayload.js.

import { companyAddress, branchShipTo, addressLines, hasUsableAddress } from './addresses';

// The Purchasing domain colour, locked in CLAUDE.md. Stated once here rather
// than typed into each renderer.
export const PO_ACCENT = '#EF9F27';

// ---------------------------------------------------------------------------
// Money and text
// ---------------------------------------------------------------------------

// Currency for a document, not for a screen. Always two decimals, always a
// thousands separator: "$1,437.50" and never "$1437.5". A purchase order is
// read by a stranger's accounts department and a ragged number reads as
// amateur in a way a missing suite number does not.
export function money(n) {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0;
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// EVERY piece of text in this document is escaped before it reaches HTML, and
// that is not a formality. The vendor name, the item names, the PO reference
// and -- most of all -- the free-text note Jennifer types are all under a
// person's control, and the output is an HTML email sent OUTSIDE the company.
// An unescaped `<` would at best break the document and at worst carry markup
// into a stranger's inbox under D&R's verified domain.
export function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// A date as a document states it -- "Aug 30, 2026", never an ISO string. Takes
// a Date, an ISO string, or nothing.
export function documentDate(value) {
  const d = value instanceof Date ? value : (value ? new Date(value) : new Date());
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// The blockers -- what makes an order unsendable, in the reader's words
// ---------------------------------------------------------------------------

// Gate callout G, and Screen 0's rule that "a branch with no address cannot
// have an order sent from it". The button is BLOCKED with the reason shown, not
// refused after the click: every error banner in this page renders hundreds of
// pixels above the control that triggered it, so a refusal reads as a dead
// button rather than as a refusal. That exact confusion is already logged
// against four other forms in this file.
//
// Each entry says what is missing AND why it matters, because the person
// reading it is deciding whether to go and fix it or send anyway. "Vendor has
// no email address" is actionable; "invalid" is not.
export function purchaseOrderBlockers({ vendor, branch, settings, buyer }) {
  const blockers = [];

  // FOUND BY LIVE TEST, Session 78n8.2. These read as one condition and are two,
  // and the merged version said the wrong thing in the commoner case: before a
  // vendor is chosen at all, the sheet reported "This vendor has no email
  // address on file" -- naming a vendor that does not exist and sending the
  // buyer off to fix a vendor record when the actual next step is to pick one.
  if (!vendor) {
    blockers.push('No vendor is selected yet, so there is nowhere to send the order.');
  } else if (!vendor.email || !String(vendor.email).trim()) {
    blockers.push(`${vendor.name || 'This vendor'} has no email address on file, so there is nowhere to send the order.`);
  }

  // D4: the send is refused if the sender has no email on file. In practice
  // users.email is not-nullable and is what people sign in with, so this should
  // be unreachable -- it is here because "should be unreachable" is how a
  // document goes out with an empty Reply-To that nobody notices until a vendor
  // replies into the void.
  if (!buyer?.email || !String(buyer.email).trim()) {
    blockers.push('Your account has no email address, so the vendor would have nowhere to reply.');
  }

  const shipTo = branchShipTo(branch, settings);
  if (!hasUsableAddress(shipTo)) {
    blockers.push(`${branch?.name || 'This location'} has no address yet, so the vendor would have nowhere to ship to. Add it in Company settings.`);
  }

  if (!hasUsableAddress(companyAddress(settings))) {
    blockers.push('Your company address is not set, so the order would print without a letterhead. Add it in Company settings.');
  }

  return blockers;
}

// ---------------------------------------------------------------------------
// The model
// ---------------------------------------------------------------------------

// `lines` is this module's own contract rather than the page's row shape, on
// purpose: the caller maps material_request_items into it once, and every test
// in purchaseOrder.test.js can then build an order without a database. Each
// line is { name, sku, qty, unit, pricePerPack }.
//
// The line total is COMPUTED here and never accepted from the caller. Two
// places multiplying quantity by price is two places to disagree, and the one
// that disagrees silently is the one printed on the document the vendor
// invoices against.
export function buildPurchaseOrder({
  poRef,
  requestNumber,
  workOrderRef,
  vendor,
  branch,
  settings,
  buyer,
  lines = [],
  note,
  orderedAt,
}) {
  const cleanLines = (lines || [])
    .filter(Boolean)
    .map(l => {
      const qty = Number(l.qty) || 0;
      const price = Number(l.pricePerPack) || 0;
      return {
        name: l.name || 'Item',
        sku: l.sku || '',
        qty,
        unit: l.unit || '',
        pricePerPack: price,
        total: qty * price,
      };
    });

  const orderTotal = cleanLines.reduce((sum, l) => sum + l.total, 0);

  return {
    poRef: poRef || '',
    requestNumber: requestNumber || '',
    workOrderRef: workOrderRef || '',
    orderedOn: documentDate(orderedAt),

    company: companyAddress(settings),
    shipTo: branchShipTo(branch, settings),

    vendor: {
      name: vendor?.name || '',
      contactName: vendor?.contact_name || '',
      email: vendor?.email || '',
      paymentTerms: vendor?.payment_terms || '',
    },

    buyer: {
      name: buyer?.full_name || '',
      email: buyer?.email || '',
    },

    lines: cleanLines,
    orderTotal,
    note: (note || '').trim(),
  };
}

// ---------------------------------------------------------------------------
// What the vendor must write on their paperwork
// ---------------------------------------------------------------------------

// Colby's ask at 78n8.2: the document must tell the vendor what to put on the
// packing slip and the invoice, so a receiving team member holding a box and an
// AP clerk holding a bill can each identify which order it belongs to.
//
// IT ASKS FOR THE PO NUMBER AND NOTHING ELSE, and that is a decision rather than
// a shortcut. Stede GENERATES one PO number per material request (#287), so the
// PO number already identifies the request uniquely -- a split produces a child
// request with its own PO. Asking a vendor to ALSO print our internal request
// number means asking them to carry a second reference that means nothing to
// them, and the one thing suppliers reliably do with extra reference fields is
// drop them. SAP, Coupa and NetSuite all key three-way matching on the PO
// number alone for the same reason.
//
// VERIFIED RATHER THAN ASSUMED, because the ask is only worth making if Stede
// can act on what comes back: the Receive stock screen searches item, PO, MR#
// and WO# (InventoryActionCatalog.js), and Invoicing searches po_ref,
// request_number, vendor_order_ref and vendor name (InvoicingPage.js). Both
// ends can find an order by the number this sentence asks for.
//
// It names BOTH documents explicitly. "Reference PO 1234 on your invoice" --
// the sentence this replaces -- says nothing about the packing slip, which is
// the half receiving needs and the half that arrives first.
//
// One function, used by the HTML, the PDF and the plain-text draft, because a
// reference instruction present on two routes out of three is how an unlabelled
// box arrives from the third.
export function referenceInstruction(doc) {
  if (!doc?.poRef) return '';
  return `Please print PO ${doc.poRef} on your packing slip and on your invoice - it is how we match your shipment and your bill to this order.`;
}

// ---------------------------------------------------------------------------
// The HTML renderer -- the vendor's email body, and the on-screen preview
// ---------------------------------------------------------------------------

// TABLE-BASED AND INLINE-STYLED, deliberately, and it is not laziness. This
// exact markup has to survive Outlook's word-processor renderer, which drops
// external stylesheets, ignores most `class` styling, and mispositions 1px
// borders inside inline spans. src/lib/notify.js's emailWrapper already learned
// that the hard way and says so; this follows the same rules rather than
// inventing a second, prettier approach that renders correctly nowhere.
//
// It is ALSO the preview Jennifer sees, rendered into the sheet. One markup,
// so what she approves is literally what the vendor receives -- a preview drawn
// with different code is a preview that can lie.

const BORDER = '#E3E3E3';
const INK = '#1A1A1A';
const MUTED = '#6B6B6B';

function addressBlockHtml(heading, addr, opts = {}) {
  const lines = addressLines(addr, opts);

  // TESTED INTO EXISTENCE, and the test found a real defect: an address block is
  // judged on whether it can be DELIVERED TO, never on whether it rendered any
  // text. A branch with no street still has a name, so the naive check -- "did
  // addressLines return anything?" -- drew "D&R Signs - Houston" and looked
  // complete. That is exactly the failure the gate names: a document reading
  // "Ship to: Dallas" that no carrier can deliver against, and it is worse than
  // an empty block because nothing about it invites a second look.
  const usable = hasUsableAddress(addr);
  const body = usable
    ? lines.map(l => esc(l)).join('<br />')
    : (lines.length ? lines.map(l => esc(l)).join('<br />') + '<br />' : '')
      + '<span style="color:#B00020;">Address not set</span>';
  return `
    <td style="vertical-align:top;padding:0 16px 0 0;font-size:13px;line-height:1.5;color:${INK};">
      <div style="font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:${MUTED};margin-bottom:6px;">${esc(heading)}</div>
      ${body}
    </td>`;
}

export function renderPurchaseOrderHtml(doc) {
  const rows = doc.lines.map(l => `
        <tr>
          <td style="padding:10px 8px;border-bottom:1px solid ${BORDER};font-size:13px;color:${INK};">${esc(l.name)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid ${BORDER};font-size:13px;color:${MUTED};">${esc(l.sku)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid ${BORDER};font-size:13px;color:${INK};text-align:right;">${esc(l.qty)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid ${BORDER};font-size:13px;color:${MUTED};">${esc(l.unit)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid ${BORDER};font-size:13px;color:${INK};text-align:right;">${money(l.pricePerPack)}</td>
          <td style="padding:10px 8px;border-bottom:1px solid ${BORDER};font-size:13px;color:${INK};text-align:right;font-weight:600;">${money(l.total)}</td>
        </tr>`).join('');

  // The reference block only draws the rows it has. An empty "Job" line on a
  // document sent outside the company reads as something having gone missing.
  const refLines = [];
  if (doc.requestNumber) refLines.push('Request ' + doc.requestNumber);
  if (doc.workOrderRef) refLines.push('Job ' + doc.workOrderRef);
  if (doc.vendor.paymentTerms) refLines.push('Terms: ' + doc.vendor.paymentTerms);

  const noteBlock = doc.note ? `
      <tr>
        <td colspan="6" style="padding:16px 8px 0;">
          <div style="background:#FFF8EC;border-left:3px solid ${PO_ACCENT};padding:12px 14px;font-size:13px;line-height:1.6;color:${INK};">
            ${esc(doc.note).replace(/\n/g, '<br />')}
          </div>
        </td>
      </tr>` : '';

  const vendorLines = [doc.vendor.name, doc.vendor.contactName ? 'Attn: ' + doc.vendor.contactName : '', doc.vendor.email]
    .filter(Boolean).map(l => esc(l)).join('<br />');

  return `
<table width="100%" cellpadding="0" cellspacing="0" style="background:#FFFFFF;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
  <tr>
    <td style="padding:0 0 20px;border-bottom:3px solid ${PO_ACCENT};">
      <table width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td style="vertical-align:top;font-size:13px;line-height:1.5;color:${INK};">
            <div style="font-size:20px;font-weight:700;letter-spacing:0.3px;">${esc(doc.company?.name || '')}</div>
            ${addressLines(doc.company, { includeName: false }).map(l => esc(l)).join('<br />')}
          </td>
          <td style="vertical-align:top;text-align:right;">
            <div style="font-size:22px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:${INK};">Purchase Order</div>
            <div style="font-size:13px;color:${MUTED};line-height:1.6;margin-top:4px;">
              ${doc.poRef ? 'PO ' + esc(doc.poRef) + '<br />' : ''}
              Ordered ${esc(doc.orderedOn)}<br />
              ${doc.buyer.name ? 'Buyer: ' + esc(doc.buyer.name) : ''}
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>

  <tr>
    <td style="padding:20px 0;">
      <table width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td style="vertical-align:top;padding:0 16px 0 0;font-size:13px;line-height:1.5;color:${INK};">
            <div style="font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:${MUTED};margin-bottom:6px;">Vendor</div>
            ${vendorLines || '<span style="color:#B00020;">Not set</span>'}
          </td>
          ${addressBlockHtml('Ship to', doc.shipTo)}
          <td style="vertical-align:top;font-size:13px;line-height:1.5;color:${INK};">
            <div style="font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:${MUTED};margin-bottom:6px;">Reference</div>
            ${refLines.length ? refLines.map(l => esc(l)).join('<br />') : '&mdash;'}
          </td>
        </tr>
      </table>
    </td>
  </tr>

  <tr>
    <td>
      <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">
        <tr>
          <th align="left"  style="padding:8px;border-bottom:2px solid ${INK};font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:${MUTED};">Item</th>
          <th align="left"  style="padding:8px;border-bottom:2px solid ${INK};font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:${MUTED};">SKU</th>
          <th align="right" style="padding:8px;border-bottom:2px solid ${INK};font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:${MUTED};">Qty</th>
          <th align="left"  style="padding:8px;border-bottom:2px solid ${INK};font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:${MUTED};">Unit</th>
          <th align="right" style="padding:8px;border-bottom:2px solid ${INK};font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:${MUTED};">Price / pack</th>
          <th align="right" style="padding:8px;border-bottom:2px solid ${INK};font-size:10px;letter-spacing:1.2px;text-transform:uppercase;color:${MUTED};">Total</th>
        </tr>
        ${rows}
        <tr>
          <td colspan="4"></td>
          <td style="padding:14px 8px 0;text-align:right;font-size:11px;letter-spacing:1.2px;text-transform:uppercase;color:${MUTED};">Order total</td>
          <td style="padding:14px 8px 0;text-align:right;font-size:18px;font-weight:700;color:${INK};">${money(doc.orderTotal)}</td>
        </tr>
        ${noteBlock}
      </table>
    </td>
  </tr>

  ${doc.poRef ? `<tr>
    <td style="padding:22px 0 0;">
      <div style="border:1px solid ${PO_ACCENT};background:#FFF8EC;border-radius:6px;padding:12px 14px;">
        <div style="font-size:10px;letter-spacing:1.5px;text-transform:uppercase;color:#8A5A00;font-weight:700;margin-bottom:5px;">On your packing slip and invoice</div>
        <div style="font-size:13px;line-height:1.6;color:${INK};">${esc(referenceInstruction(doc))}</div>
      </div>
    </td>
  </tr>` : ''}

  <tr>
    <td style="padding:20px 0 0;border-top:1px solid ${BORDER};">
      <div style="font-size:12px;line-height:1.7;color:${MUTED};padding-top:16px;">
        Please confirm receipt and expected ship date by reply.
      </div>
    </td>
  </tr>
</table>`;
}

// ---------------------------------------------------------------------------
// The plain-text order, for the "Draft in my email" route
// ---------------------------------------------------------------------------

// D3 keeps the mail-client draft as the SECOND route rather than dropping it,
// and F4 is why it cannot be the first: a mailto: body is plain text in every
// major client, and Outlook stops opening the link at around 1,026 characters,
// which fails as "the button did nothing" rather than as an error.
//
// So this deliberately builds the SHORTEST honest order rather than a text
// transcription of the document above. The addresses are omitted -- they are
// the longest part and the vendor already knows where D&R is once the PO
// reference is in hand -- and the length is reported to the caller so the UI
// can warn before it hands over a link that will silently fail.
export const MAILTO_SAFE_CHARS = 1000;

export function renderPurchaseOrderText(doc) {
  const out = [];
  out.push(`Purchase Order${doc.poRef ? ' ' + doc.poRef : ''}`);
  if (doc.company?.name) out.push(doc.company.name);
  out.push('');
  doc.lines.forEach(l => {
    const sku = l.sku ? ` (${l.sku})` : '';
    out.push(`${l.qty} ${l.unit} - ${l.name}${sku} @ ${money(l.pricePerPack)} = ${money(l.total)}`);
  });
  out.push('');
  out.push(`Order total: ${money(doc.orderTotal)}`);
  if (doc.shipTo) {
    const ship = addressLines(doc.shipTo, { includePhone: false }).join(', ');
    if (ship) out.push(`Ship to: ${ship}`);
  }
  if (doc.note) { out.push(''); out.push(doc.note); }
  out.push('');
  // Colby's catch at 78n8.2: without this, the draft route sends an order with
  // no reference instruction at all, so the vendor ships an unlabelled box and
  // receiving has nothing to match it against. The instruction survives every
  // route, not only the formatted one.
  const ref = referenceInstruction(doc);
  if (ref) { out.push(ref); out.push(''); }
  out.push('Please confirm receipt and expected ship date by reply.');
  return out.join('\n');
}

// The mailto: URL, plus whether it is over the length at which Outlook stops
// opening links. The caller decides what to do about it; this only measures,
// because the ceiling is a property of the recipient's mail client and not
// something this module can fix.
export function buildMailtoLink(doc) {
  const subject = `Purchase Order${doc.poRef ? ' ' + doc.poRef : ''}${doc.company?.name ? ' — ' + doc.company.name : ''}`;
  const bodyText = renderPurchaseOrderText(doc);
  const href = `mailto:${encodeURIComponent(doc.vendor.email || '')}`
    + `?subject=${encodeURIComponent(subject)}`
    + `&body=${encodeURIComponent(bodyText)}`;
  return { href, length: href.length, tooLongForOutlook: href.length > MAILTO_SAFE_CHARS };
}

// The subject line of the sent order. One place, because it appears on the
// vendor's copy and on the buyer's own bcc copy and they must match.
export function purchaseOrderSubject(doc) {
  const co = doc.company?.name ? ` from ${doc.company.name}` : '';
  return `Purchase Order${doc.poRef ? ' ' + doc.poRef : ''}${co}`;
}

// The PDF's filename. Falls back to the request number, then to a bare name --
// never to "undefined.pdf", which is what a vendor would otherwise file.
export function purchaseOrderFilename(doc) {
  const stem = doc.poRef || doc.requestNumber || 'purchase-order';

  // THE DOT IS DELIBERATELY NOT IN THE ALLOWED SET, and a test is what caught
  // it. Permitting dots let "../../etc/passwd" through as "..-..-etc-passwd.pdf"
  // -- and functions/_shared/mailPayload.js REFUSES any filename containing
  // "..", so the send would have failed at the mail function with an error
  // naming a path nobody typed. A PO reference is a reference, not a path: it
  // has no legitimate need of a dot, and the only extension here is the one
  // appended below.
  const safe = String(stem).replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  return (safe || 'purchase-order') + '.pdf';
}
